import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { setUser } from "../../../redux/userSlice";
import { setProfileComplete } from "../../../redux/authSlice";
import {
  clearDirty,
  loadProfileData,
  resetForm,
  setCurrentStep,
} from "../../../redux/profileFormSlice";
import { fetchUserProfile, submitUserProfile, updateUserProfile } from "../../../services/profileService";
import { steps, safeAppend } from "../../../utils/profileHelpers";
import getCareerIdFromCSV from "../../../utils/getCareerIdFromCSV";
import Sidebar from "../../../components/Sidebar";
import ProgressBar from "../../../components/profile/ProgressBar";
import ProgressSidebar from "../../../components/profile/ProgressSidebar";
import NavigationButtons from "../../../components/profile/NavigationButtons";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  const userId = useSelector((state) => state.auth.id);
  const isWelcomeSeen = useSelector((state) => state.auth.isWelcomeSeen);
  const isProfileComplete = useSelector((state) => state.auth.isProfileComplete);
  const { currentStep, isFormLoaded, ...profileFormData } = useSelector((state) => state.profileForm);
  const CurrentStepComponent = steps.find(s => s.id === currentStep)?.component || steps[0].component;

  useEffect(() => {
    if (!userId || isFormLoaded) return;

    const timeout = setTimeout(() => {
      setLoadingTimeout(true);
      dispatch(loadProfileData({}));
      dispatch(setCurrentStep(1));
    }, 10000);

    fetchUserProfile(userId)
      .then(({ data }) => {
        clearTimeout(timeout);
        dispatch(loadProfileData(data));
        dispatch(setUser(data));
      })
      .catch(() => {
        clearTimeout(timeout);
        dispatch(loadProfileData({}));
        dispatch(setCurrentStep(1));
      });
  }, [userId, isFormLoaded, dispatch]);

  useEffect(() => {
    if (!isFormLoaded) dispatch(setCurrentStep(1));
  }, [isFormLoaded, dispatch]);

  const handleStepNavigation = (stepId) => dispatch(setCurrentStep(stepId));

  const appendFiles = (formData, files = {}) => {
    const fileKeys = ['profileImage', 'cv'];
    
    fileKeys.forEach(key => {
      const file = files[key];
      if (file === undefined || file === null) {
        formData.append(key, "");
      } else {
        const value = Array.isArray(file) ? file[0] : file;
        if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === "string") {
          formData.append(key, value);
        } else {
          formData.append(key, "");
        }
      }
    });
  };

  const handleProfileSubmit = async () => {
    setIsSubmitting(true);
    try {
      const step = profileFormData;
      const occupationId = step.step5?.currentJob
        ? await getCareerIdFromCSV(step.step5.currentJob)
        : null;

      const fields = {
        // Step 1
        currentSituation: step.step1?.currentSituation,
        // Step 2
        interestReason: step.step2?.interestReason,
        // Step 3
        firstName: step.step3?.firstName,
        lastName: step.step3?.lastName,
        gender: step.step3?.gender,
        country: step.step3?.country,
        stateOrProvince: step.step3?.state,
        city: step.step3?.city,
        age: step.step3?.age,
        race: step.step3?.race,
        email: step.step3?.email,
        // Step 4
        highestLevelEducation: step.step4?.education,
        employmentStatus: step.step4?.status,
        professionalExperience: step.step4?.experience,
        yearsOfExperience: step.step4?.workYears,
        // Step 5
        currentJob: step.step5?.currentJob,
        industry: step.step5?.industry,
        // commonSkills: step.step5?.commonSkills,
        // definingSkills: step.step5?.definingSkills,
        // distinguishingSkills: step.step5?.distinguishingSkills,
        // salaryBoostingSkills: step.step5?.salaryBoostingSkills,
        // certifications: step.step5?.certifications,
        // Step 6
        // AILiteracySkills: step.step6?.AILiteracySkills,
        // appliedAISkills: step.step6?.appliedAISkills,
        // aiCareers: step.step6?.aiCareers,
        // ethicsResponsibleAI: step.step6?.ethicsResponsibleAI,
        // softAISkills: step.step6?.softAISkills,
        // strategicAISkills: step.step6?.strategicAISkills,
        // technicalAISkills: step.step6?.technicalAISkills,
        // ai_application: step.step6?.ai_application,
        // Additional
        emergingAISkills: step.additionalData?.emergingAISkills,
        occupationId,
        currentStep,
      };

      let data;
      const formData = new FormData();
      formData.append("userId", userId);
      Object.entries(fields).forEach(([key, val]) => safeAppend(formData, key, val));
      appendFiles(formData, step.step6); 

      if (isProfileComplete) {
        const { data: updateData } = await updateUserProfile(userId, formData);
        data = updateData;
        toast.success("Profile is updated");
      } else {      
        const { data: submitData } = await submitUserProfile(formData);
        data = submitData;
        toast.success("Profile is created");
      }

      const updatedUserData = {
        currentJob: data?.currentJob ?? step.step6?.currentJob,
        profilePhoto: data?.profilePhoto,
        currentJobDescription: data?.currentJobDescription,
        occupationId: data?.occupationId,
      };
      
      dispatch(setUser(updatedUserData));
      
      dispatch(setProfileComplete(data?.isProfileComplete ?? true));
      dispatch(clearDirty());
      dispatch(resetForm());

      navigate(isWelcomeSeen ? "/dashboard/home" : "/dashboard/welcome");
    } catch (err) {
      console.error("❌ Error submitting profile:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!userId) return <Navigate to="/auth/login" replace />;
  if (!isFormLoaded && !loadingTimeout) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin h-12 w-12 border-b-2 border-purple-600 rounded-full mx-auto mb-4" />
            <p className="text-gray-600">Loading profile setup...</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:flex-1">
          <ProgressBar />
          <div className="rounded-lg border border-gray-300 shadow-md p-4 sm:p-6">
            {!isFormLoaded ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin h-12 w-12 border-b-2 border-purple-600 rounded-full mx-auto mb-4" />
                  <p className="text-gray-600">Loading your profile data...</p>
                </div>
              </div>
            ) : (
              <>
                <CurrentStepComponent />
                <NavigationButtons onSubmit={handleProfileSubmit} isSubmitting={isSubmitting} />
              </>
            )}
          </div>
        </div>
        <div className="w-full lg:w-72 lg:shrink-0">
          <ProgressSidebar onStepNavigation={handleStepNavigation} />
        </div>
      </div>
    </Sidebar>
  );
};

export default Profile;
